import type { WorkspaceLimit } from "@app/components/app/ReachedLimitPopup";
import type { InputBarContainerProps } from "@app/components/assistant/conversation/input_bar/InputBarContainer";
import type { AgentLoopToolNotificationEvent } from "@app/lib/actions/mcp";
import type { ProgressNotificationContentType } from "@app/lib/actions/mcp_internal_actions/output_schemas";
import type { AgentMessageFeedbackType } from "@app/lib/api/assistant/feedback";
import type { AgentMessageEvents } from "@app/lib/api/assistant/streaming/types";
import type { DustError } from "@app/lib/error";
import type { AgentMCPActionType } from "@app/types/actions";
import type { LightAgentConfigurationType } from "@app/types/assistant/agent";
import type {
  CompactionMessageType,
  ConversationWithoutContentType,
  InlineActivityStep,
  LightAgentMessageType,
  LightAgentMessageWithActionsType,
  LightMessageType,
  UserMessageOrigin,
  UserMessageTypeWithContentFragments,
} from "@app/types/assistant/conversation";
import {
  HIDDEN_REPLY_ORIGINS,
  isCompactionMessageType,
  isHiddenMessageOrigin,
  isLightAgentMessageType,
  isLightAgentMessageWithActionsType,
  isTerminalAgentMessageStatus,
  isUserMessageTypeWithContentFragments,
} from "@app/types/assistant/conversation";
import type { RichMention } from "@app/types/assistant/mentions";
import type { ContentFragmentsType } from "@app/types/content_fragment";
import type { LiveHuddleType } from "@app/types/live_docs";
import type { ModelId } from "@app/types/shared/model_id";
import type { Result } from "@app/types/shared/result";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import type { MutableRefObject, ReactNode } from "react";
import type { Components } from "react-markdown";
import type { PluggableList } from "react-markdown/lib/react-markdown";

export type AgentStateClassification =
  | "placeholder"
  | "thinking"
  | "acting"
  | "writing"
  | "done";

export type ActionProgressState = Map<
  ModelId,
  {
    action: AgentMCPActionType;
    progress?: ProgressNotificationContentType;
  }
>;

export type PendingToolCall = {
  toolName: string;
  toolCallId?: string;
  toolCallIndex?: number;
};

export function getPendingToolCallKey(
  pendingToolCall: PendingToolCall,
  index: number
): string {
  if (pendingToolCall.toolCallId) {
    return `id-${pendingToolCall.toolCallId}`;
  }

  if (pendingToolCall.toolCallIndex !== undefined) {
    return `index-${pendingToolCall.toolCallIndex}`;
  }

  return `name-${pendingToolCall.toolName}-${index}`;
}

export type AgentMessageWithStreaming = LightAgentMessageWithActionsType & {
  streaming: {
    agentState: AgentStateClassification;
    isRetrying: boolean;
    lastUpdated: Date;
    actionProgress: ActionProgressState;
    pendingToolCalls: PendingToolCall[];
    inlineActivitySteps: InlineActivityStep[];
  };
};

export type ConversationForkNotice = {
  type: "conversation_fork_notice";
  sId: string;
  created: number;
  rank: number;
  visibility: "visible";
  sourceMessageId: string;
  childConversationId: string;
  childConversationTitle: string | null;
  user: UserType;
};

// A huddle shown in the chat at the point it started; not a message.
export type ConversationHuddleNotice = {
  type: "conversation_huddle_notice";
  sId: string;
  created: number;
  rank: number;
  visibility: "visible";
  huddle: LiveHuddleType;
};

export type AgentMessageStateEvent = (
  | AgentMessageEvents
  | AgentLoopToolNotificationEvent
) & { step: number };

export type AgentMessageStateWithControlEvent =
  | AgentMessageStateEvent
  | { type: "end-of-stream" };

export type VirtuosoMessage =
  | AgentMessageWithStreaming
  | UserMessageTypeWithContentFragments
  | CompactionMessageType
  | ConversationForkNotice
  | ConversationHuddleNotice;

export type VirtuosoMessageListContext = {
  owner: LightWorkspaceType;
  user: UserType;
  isOnboardingConversation: boolean;
  handleSubmit: (
    input: string,
    mentions: RichMention[],
    contentFragments: ContentFragmentsType
  ) => Promise<Result<undefined, DustError>>;
  draftKey: string;
  conversation?: ConversationWithoutContentType;
  agentBuilderContext?: {
    draftAgent?: LightAgentConfigurationType;
    isSubmitting: boolean;
    actionsToShow: InputBarContainerProps["actions"];
    // Locks the conversation to its current agent: no `@` agent suggestions
    // and no agent switch on paste (used by the sidekick).
    disableAgentMentions?: boolean;
    resetConversation: () => void;
    clientSideMCPServerIds?: string[];
    skipToolsValidation?: boolean;
  };
  feedbacksByMessageId: Record<string, AgentMessageFeedbackType>;
  additionalMarkdownComponents?: Components;
  additionalMarkdownPlugins?: PluggableList;
  // Project membership fields (undefined for non-project conversations)
  isProjectMember?: boolean;
  isProjectRestricted?: boolean;
  isProjectArchived?: boolean;
  projectId?: string;
  projectSpaceName?: string;
  isAutoScrollEnabledRef: MutableRefObject<boolean>;
  isNoSeat?: boolean;
  setLimitReachedCode?: (code: WorkspaceLimit) => void;
  inputPlaceholder?: string;
  stickyMentions?: RichMention[];
  composerBlockMessage?: string | null;
  composerAccessory?: ReactNode;
  // No agent answers here (the founders chat), so the composer addresses people only.
  isPeopleOnly?: boolean;
  // When the list continues an earlier thread, its first message only opens a
  // new day if it is on a different day than this.
  precedingMessageAt?: number;
};

export const areSameRank = (
  a: VirtuosoMessage,
  b: VirtuosoMessage
): boolean => {
  return a.rank === b.rank;
};

export const getPredicateForRank = (
  m: VirtuosoMessage
): ((m: VirtuosoMessage) => boolean) => {
  return (m2: VirtuosoMessage) => areSameRank(m, m2);
};

export const isTriggeredOrigin = (origin?: UserMessageOrigin | null) => {
  return (
    origin && (origin === "triggered" || origin === "triggered_programmatic")
  );
};

// Central helper to control which user message should be hidden in the UI.
// Extend this list as we introduce more bootstrap/system user messages.
export const isHiddenMessage = (message: VirtuosoMessage): boolean => {
  return (
    (isUserMessage(message) &&
      (isHiddenMessageOrigin(message.context.origin) ||
        isSidekickBootstrapMessage(message))) ||
    isAgenticCarrierUserMessage(message)
  );
};

// An agent's reply to a message whose origin hides its replies too (work asked for in a huddle):
// it lives in the room, and the chat shows the huddle's card instead.
export const isHiddenReply = (
  messages: VirtuosoMessage[],
  message: VirtuosoMessage
): boolean =>
  isAgentMessageWithStreaming(message) &&
  messages.some(
    (parent) =>
      parent.sId === message.parentMessageId &&
      isUserMessage(parent) &&
      HIDDEN_REPLY_ORIGINS.includes(parent.context.origin)
  );

export const isCompactionMessage = (
  msg: VirtuosoMessage
): msg is CompactionMessageType => msg.type === "compaction_message";

export const isConversationForkNotice = (
  msg: VirtuosoMessage
): msg is ConversationForkNotice => msg.type === "conversation_fork_notice";

export const isConversationHuddleNotice = (
  msg: VirtuosoMessage
): msg is ConversationHuddleNotice => msg.type === "conversation_huddle_notice";

// Rows the chat draws between messages that are not messages themselves.
export const isConversationNotice = (
  msg: VirtuosoMessage
): msg is ConversationForkNotice | ConversationHuddleNotice =>
  isConversationForkNotice(msg) || isConversationHuddleNotice(msg);

export const isUserMessage = (
  msg: VirtuosoMessage
): msg is UserMessageTypeWithContentFragments =>
  "type" in msg && msg.type === "user_message" && "contentFragments" in msg;

export const isHandoverUserMessage = (msg: VirtuosoMessage): boolean =>
  isUserMessage(msg) && msg.agenticMessageData?.type === "agent_handover";

/**
 * The carrier message a delegating agent posts to summon a delegate. Hidden like
 * a handover carrier, but it must not stop the delegating agent's own stream:
 * delegation keeps the floor where handover gives it away.
 */
export const isDelegateUserMessage = (msg: VirtuosoMessage): boolean =>
  isUserMessage(msg) && msg.agenticMessageData?.type === "agent_delegate";

/**
 * The carrier a peer-summoning agent posts for a lateral mention. The summons is
 * already legible without it: the mentioning agent's own message carries the
 * `@peer` that caused the dispatch, so rendering the carrier only repeats that
 * mention as a second bubble under the first.
 */
export const isLateralUserMessage = (msg: VirtuosoMessage): boolean =>
  isUserMessage(msg) && msg.agenticMessageData?.type === "agent_lateral";

/**
 * A user message an agent posted to summon another agent into this conversation.
 * It is machinery, not conversation: hide it, and never read the user's intent
 * (last message, last mentioned agent) off it — doing so would let a delegate
 * inherit the default-responder slot that belongs to the delegating agent.
 */
export const isAgenticCarrierUserMessage = (msg: VirtuosoMessage): boolean =>
  isHandoverUserMessage(msg) ||
  isDelegateUserMessage(msg) ||
  isLateralUserMessage(msg);

/**
 * Optimistic rows created in ConversationViewer before the backend responds.
 * Identified by the sId prefixes from createPlaceholderUserMessage /
 * createPlaceholderAgentMessage.
 */
export const isPlaceholderMessage = (msg: VirtuosoMessage): boolean =>
  msg.sId.startsWith("placeholder-user-message-") ||
  msg.sId.startsWith("placeholder-agent-message-");

export const isAgentMessageWithStreaming = (
  msg: VirtuosoMessage
): msg is AgentMessageWithStreaming =>
  "streaming" in msg && msg.type === "agent_message";

export const getMessageDate = (msg: VirtuosoMessage): Date =>
  new Date(msg.created);

export const makeInitialMessageStreamState = (
  message: LightAgentMessageType | LightAgentMessageWithActionsType
): AgentMessageWithStreaming => {
  return {
    ...message,
    actions: isLightAgentMessageWithActionsType(message) ? message.actions : [],
    streaming: {
      actionProgress: new Map(),
      agentState: message.status === "created" ? "thinking" : "done",
      // Live messages rebuild inline steps from the SSE replay on mount.
      inlineActivitySteps:
        message.status === "created" ? [] : (message.activitySteps ?? []),
      isRetrying: false,
      lastUpdated: new Date(),
      pendingToolCalls: [],
    },
  };
};

// Returns true when the message still matches the output of makeInitialMessageStreamState —
// i.e. no SSE event has moved it beyond the initial "thinking" state yet.
// The rest-spread exhaustiveness check below ensures that adding a new field to
// AgentMessageWithStreaming["streaming"] without handling it here is a compile error.
export const isAtInitialStreamState = (
  msg: AgentMessageWithStreaming
): boolean => {
  const {
    agentState,
    isRetrying,
    pendingToolCalls,
    inlineActivitySteps,
    actionProgress,
    lastUpdated: _lastUpdated, // always "now" at creation; not comparable
    ...rest
  } = msg.streaming;

  // Fails to compile if a new streaming field is added without an explicit
  // decision about whether to check it here.
  void (rest satisfies Record<PropertyKey, never>);

  return (
    !isTerminalAgentMessageStatus(msg.status) &&
    msg.content === null &&
    msg.chainOfThought === null &&
    agentState === "thinking" &&
    !isRetrying &&
    pendingToolCalls.length === 0 &&
    inlineActivitySteps.length === 0 &&
    actionProgress.size === 0
  );
};

const isSidekickBootstrapMessage = (
  message: UserMessageTypeWithContentFragments
): boolean => {
  return message.context.origin === "agent_sidekick" && message.rank === 0;
};

export const convertLightMessageTypeToVirtuosoMessages = (
  messages: LightMessageType[]
): VirtuosoMessage[] =>
  messages.map((message) => {
    if (isCompactionMessageType(message)) {
      return message;
    } else if (isUserMessageTypeWithContentFragments(message)) {
      return message;
    } else if (isLightAgentMessageWithActionsType(message)) {
      return makeInitialMessageStreamState(message);
    } else if (isLightAgentMessageType(message)) {
      return makeInitialMessageStreamState(message);
    } else {
      assertNeverAndIgnore(message);
      return message; // Non reachable
    }
  });
